import { useCallback } from 'react';

import { enqueueSnackbar } from 'notistack';

import callAzureFunction from 'utils-ts/call-azure-function';
import callAzureFunctionPublic from 'utils-ts/call-azure-function-public';
import handleError from 'utils-ts/handle-error';
import { useSelector } from 'react-redux';
import {
  appConfigPaths,
  cleanInvoiceSectionInvoiceEditor,
  type AppConfigRoot,
  type InvoicePdfData,
  type BankDetails,
  type Invoice
} from '@a1/shared';

const useInvoicePdf = () => {
  const pdfHeaderImgUrl = useSelector((state: AppConfigRoot) => state?.appConfig?.data?.images?.pdfHeader);

  const getBankDetails = useCallback(async () => {
    let bankDetails: BankDetails | null = null;

    try {
      const paymentsConfig = appConfigPaths.payments;

      const { data } = await callAzureFunctionPublic<{ configs: { payments: BankDetails } }>({
        url: 'public/app-configurations',
        method: 'get',
        params: {
          keys: [
            paymentsConfig.bankAccountName.path,
            paymentsConfig.bankAccountBSB.path,
            paymentsConfig.bankAccountNumber.path,
            paymentsConfig.companyName.path,
            paymentsConfig.australianBusinessNumber.path,
            paymentsConfig.companyAddress1.path,
            paymentsConfig.companyAddress2.path,
            paymentsConfig.referenceNumberHelpText.path,
            paymentsConfig.remittanceHelpText.path,
            paymentsConfig.queriesHelpText.path,
            paymentsConfig.phone.path,
            paymentsConfig.website.path
          ],
          includeLocalStorageFalse: true
        }
      });
      bankDetails = data.configs.payments;
    } catch (error) {
      const message = handleError(error);
      enqueueSnackbar({
        variant: 'error',
        message,
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }

    return bankDetails;
  }, []);

  const generateInvoicePdf = async ({
    invoiceId,
    invoiceData,
    filename
  }: {
    invoiceId?: string;
    invoiceData?: Invoice;
    filename?: string;
  }) => {
    const bankDetails = await getBankDetails();

    const cleanedInvoiceData = invoiceData
      ? {
          ...invoiceData,
          InvoiceSections: cleanInvoiceSectionInvoiceEditor(invoiceData.InvoiceSections || [])
        }
      : undefined;

    const data = {
      // filename: `invoice-${invoice.invoiceNumber}.pdf`,
      bankDetails,
      pdfHeaderImgUrl,
      invoiceId, // this will be ignored in the backend if `invoiceData` is provided
      invoiceData: cleanedInvoiceData
    };

    const res = await callAzureFunction<Blob>({
      url: 'invoice-pdf-generation',
      method: 'POST',
      responseType: 'blob',
      data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([res.data]));

    // Create a temporary <a> element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'report.pdf'); // filename
    document.body.appendChild(link);
    link.click();
  };

  const getInvoicePdfData = useCallback(
    async ({
      invoiceId,
      invoiceData,
      invoiceToken
    }: {
      invoiceId?: string;
      invoiceData?: Invoice | null;
      invoiceToken?: string;
    }): Promise<InvoicePdfData> => {
      const bankDetails = await getBankDetails();

      const cleanedInvoiceData = invoiceData
        ? {
            ...invoiceData,
            InvoiceSections: cleanInvoiceSectionInvoiceEditor(invoiceData.InvoiceSections || [])
          }
        : undefined;

      const data = {
        // filename: `invoice-${invoice.invoiceNumber}.pdf`,
        bankDetails,
        pdfHeaderImgUrl,
        invoiceId, // this will be ignored in the backend if `invoiceData` is provided
        invoiceData: cleanedInvoiceData
      };

      let pdf;

      if (invoiceToken) {
        const res = await callAzureFunctionPublic<Blob>({
          url: 'public/invoice-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data,
          headers: { Authorization: `Bearer ${invoiceToken}` }
        });
        pdf = res.data;
      } else {
        const res = await callAzureFunction<Blob>({
          url: 'invoice-pdf-generation',
          method: 'POST',
          responseType: 'blob',
          data
        });
        pdf = res.data;
      }

      const blob = new Blob([pdf], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      return {
        blob,
        url,
        bankDetails
      };
    },
    [getBankDetails, pdfHeaderImgUrl]
  );

  return { getBankDetails, generateInvoicePdf, getInvoicePdfData };
};

export default useInvoicePdf;
